import { AddressInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import { RoomFacilities } from '@valk-nx/storyblok-types/src/lib/types/content.interface';

export function postalAddressSchemeOrg(address: AddressInterface) {
	return {
		'@type': 'PostalAddress' as const,
		streetAddress:
			`${address.street} ${address.streetNumber} ${address.streetNumberAddition || ''}`.trim(),
		addressLocality: address.city,
		postalCode: address.postalCode,
		addressCountry: 'NL',
	};
}

export function brandSchemeOrg(brandId: string) {
	return {
		'@id': brandId,
		'@type': 'Organization' as const,
	};
}

export function mainEntityOfPageSchemeOrg(
	url: string,
	name: string,
	publisherId: string,
	inLanguage?: string | null,
	id?: string,
) {
	return {
		'@type': 'ItemPage' as const,
		'@id': id || url,
		url,
		name,
		...(inLanguage && { inLanguage }),
		publisher: { '@id': publisherId },
	};
}

export function collectionPageSchemeOrg(
	url: string,
	name: string,
	publisherId: string,
	mentionsId: string,
	inLanguage?: string | null,
) {
	return {
		'@type': 'CollectionPage' as const,
		'@id': `${url}#LP`,
		url,
		name,
		publisher: { '@id': publisherId, '@type': 'Organization' },
		mentions: { '@id': mentionsId },
		...(inLanguage && { inLanguage }),
	};
}

export function quantitativeValueSchemeOrg(value: number, unitCode: string) {
	return {
		'@type': 'QuantitativeValue' as const,
		value,
		unitCode,
	};
}

export function hotelContainedInPlaceSchemeOrg(
	hotelId: string,
	name: string,
	url: string,
	address: AddressInterface,
	sameAs?: string,
) {
	return {
		'@type': 'Hotel' as const,
		'@id': hotelId,
		name,
		url,
		...(sameAs && { sameAs }),
		address: postalAddressSchemeOrg(address),
	};
}

export function imageSchemeOrg(
	images: PhotoInterface[],
	width: number,
	height: number,
	useArray = false,
) {
	if (images.length === 0) {
		return undefined;
	}

	const createImageObject = (image: PhotoInterface) => ({
		'@type': 'ImageObject' as const,
		url: image.src,
		name: image.alt,
		width: {
			'@type': 'QuantitativeValue' as const,
			value: width,
			unitCode: 'E37',
		},
		height: {
			'@type': 'QuantitativeValue' as const,
			value: height,
			unitCode: 'E37',
		},
	});

	return {
		image: useArray
			? images.map(createImageObject)
			: createImageObject(images[0]),
	};
}

export function amenitySchemeOrg(amenities: RoomFacilities) {
	const { sleepingComfort, bathroom, misc, generic } = amenities;
	return {
		amenityFeature: {
			'@type': 'LocationFeatureSpecification',
			value: 'True',
			name: [
				...(sleepingComfort || []),
				...(bathroom || []),
				...(misc || []),
				...(generic || []),
			],
		},
	};
}

export function businessFunctionSchemeOrg(
	functionType: 'Sell' | 'LeaseOut' = 'Sell',
) {
	return {
		businessFunction: {
			'@type': 'BusinessFunction' as const,
			url: `http://purl.org/goodrelations/v1#${functionType}`,
		},
	};
}

export function eligibleCustomerTypeSchemeOrg() {
	return {
		eligibleCustomerType: {
			'@type': 'BusinessEntityType' as const,
			url: 'http://purl.org/goodrelations/v1#Enduser',
		},
	};
}

export function priceSpecificationSchemeOrg(
	price: PriceInterface,
	unitCode: 'DAY' | 'IE' | 'C62' = 'DAY',
	name?: string,
) {
	const amount = price.amount / 100;
	return {
		priceSpecification: {
			'@type': 'UnitPriceSpecification' as const,
			...(name && { name }),
			price: amount,
			minPrice: amount,
			priceCurrency: price.currency || 'EUR',
			...(unitCode === 'DAY'
				? { unitCode }
				: {
						referenceQuantity: {
							'@type': 'QuantitativeValue' as const,
							value: 1,
							unitCode,
						},
					}),
		},
	};
}

export function areaServedSchemeOrg(
	latitude?: number,
	longitude?: number,
	geoRadius = 0,
	placeName?: string,
	placeUrl?: string,
) {
	if (latitude === undefined || longitude === undefined) {
		return undefined;
	}

	return {
		areaServed:
			placeName && placeUrl
				? {
						'@type': 'Place' as const,
						name: placeName,
						url: placeUrl,
					}
				: {
						'@type': 'GeoCircle' as const,
						geoMidpoint: {
							'@type': 'GeoCoordinates' as const,
							latitude,
							longitude,
						},
						geoRadius,
					},
	};
}

export function acceptedPaymentMethodSchemeOrg() {
	return [
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#ByBankTransferInAdvance',
		},
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#Cash',
		},
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#DirectDebit',
		},
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#PayPal',
		},
	];
}

export function organizationSchemeOrg(name: string, url: string) {
	return {
		'@type': 'Organization' as const,
		name,
		url,
	};
}

export function offersSchemeOrg(price: PriceInterface, brand: string) {
	const amount = price.amount / 100;
	return {
		offers: {
			'@type': 'Offer' as const,
			offeredBy: { '@id': brand },
			seller: { '@id': brand },
			...businessFunctionSchemeOrg('LeaseOut'),
			...eligibleCustomerTypeSchemeOrg(),
			...priceSpecificationSchemeOrg(price, 'DAY'),
			price: amount,
			priceCurrency: price.currency || 'EUR',
			acceptedPaymentMethod: acceptedPaymentMethodSchemeOrg(),
		},
	};
}
